import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import getDispensariesV3 from "api/requests/consumerApi/getDispensariesV3";
import { DispensaryV3 } from "custom-types/DispensariesV3";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import useQueryParamState from "hooks/useQueryParamState";
import {
  getCoordinates,
  getIsValidStreetAddress,
} from "redux/selectors/location";

/**
 * Use a large take to load "all" dispensaries.
 */
const MAX_DISPENSARY_TAKE = 50;

const useDispensaries = () => {
  const [loading, setLoading] = useState<boolean>(true);

  const [deliveryDispensaries, setDeliveryDispensaries] = useState<
    DispensaryV3[]
  >([]);
  const [pickupDispensaries, setPickupDispensaries] = useState<DispensaryV3[]>(
    []
  );

  const coordinates = useSelector(getCoordinates);
  const countryCode = useDomainCountryCode();
  const isValidAddress = useSelector(getIsValidStreetAddress);

  const [dispensarySort, _setDispensarySort] =
    useQueryParamState("dispensarySort");

  // * Fetches nearest Pickup & Delivery Dispensaries.
  // * Used to check nearby availability of stores.
  const fetchDispensaries = useCallback(async () => {
    let delivery: DispensaryV3[] = [];
    let pickup: DispensaryV3[] = [];

    // * Query consumer-api for Pickup & Delivery Dispensaries simultaneously
    const [deliveryQuery, pickupQuery] = await Promise.allSettled([
      getDispensariesV3({
        deliversToPoint: true,
        flags: ["dispensary", "delivery", countryCode],
        lat: coordinates.lat,
        lon: coordinates.lon,
        merchUnitName: "delivery-platinum-placement",
        ...(dispensarySort ? { sort: dispensarySort } : {}),
        take: MAX_DISPENSARY_TAKE,
      }),
      getDispensariesV3({
        flags: ["dispensary", "pickup", countryCode],
        lat: coordinates.lat,
        lon: coordinates.lon,
        ...(dispensarySort ? { sort: dispensarySort } : {}),
        take: MAX_DISPENSARY_TAKE,
      }),
    ]);

    // * Show sponsored stores before other stores
    delivery =
      deliveryQuery.status === "fulfilled"
        ? [
            ...deliveryQuery.value.sponsoredStores,
            ...deliveryQuery.value.stores,
          ]
        : [];
    pickup =
      pickupQuery.status === "fulfilled"
        ? [...pickupQuery.value.sponsoredStores, ...pickupQuery.value.stores]
        : [];

    // * Set relevant state
    setDeliveryDispensaries(delivery);
    setPickupDispensaries(pickup);
    setLoading(false);
  }, [coordinates, countryCode, isValidAddress, dispensarySort]);

  useEffect(() => {
    fetchDispensaries();
  }, [fetchDispensaries]);

  return { deliveryDispensaries, loading, pickupDispensaries };
};

export default useDispensaries;
